import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import productData, { siteConfig } from "../Datas/productData";
import { ProductCard } from "./Shop";
import "../css/main.scss";

const heroSlides = [
  {
    image: "/img/brand/hero-workshop.jpg",
    eyebrow: "Raw selvedge denim",
    title: "Wear it hard. Make it yours.",
    copy: "Unwashed indigo denim becomes completely personal — every crease, fade and repair belongs to the wearer.",
    cta: "Shop jeans",
    link: "/shop?category=Jeans",
    align: "left",
  },
  {
    image: "/img/brand/hero-street.jpg",
    eyebrow: "Cut and sewn with purpose",
    title: "Built for the road ahead.",
    copy: "Straightforward fits, hard-wearing cloth and the kind of construction that rewards daily use.",
    cta: "Shop the collection",
    link: "/shop",
    align: "right",
  },
  {
    image: "/img/brand/hero-tops.jpg",
    eyebrow: "Workwear layers",
    title: "Shirts and jackets without the noise.",
    copy: "Quietly useful pieces in denim, chambray and herringbone twill — made to work together season after season.",
    cta: "Shop tops",
    link: "/shop?category=Tops",
    align: "left",
  },
];

const selectionTabs = ["New Arrivals", "Jeans", "Pants", "Tops", "Accessories"];

const testimonials = [
  { name: "Alex M.", text: "The fit settled in after a few days and now feels made for me. The fabric and construction are exceptional." },
  { name: "Jordan O.", text: "Proper straight-leg workwear with room to move. Substantial without feeling costume-like." },
  { name: "Sam C.", text: "The chambray is beautifully woven and the seams are as clean as anything in my wardrobe." },
  { name: "Morgan R.", text: "Simple, useful clothes executed incredibly well. That is exactly what I wanted." },
  { name: "Drew D.", text: "No nonsense denim: a good rise, a true straight line, and fabric that keeps getting better." },
  { name: "Taylor K.", text: "Measurements were accurate, the pocket bags are strong, and the leg opening is just right." },
];

function Main() {
  const [activeHero, setActiveHero] = useState(0);
  const [activeSelection, setActiveSelection] = useState("New Arrivals");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterComplete, setNewsletterComplete] = useState(false);

  useEffect(() => {
    document.title = `${siteConfig.brandName} | Raw denim & workwear`;
    const timer = window.setInterval(
      () => setActiveHero((current) => (current + 1) % heroSlides.length),
      7000
    );
    return () => window.clearInterval(timer);
  }, []);

  const selectedProducts = useMemo(() => {
    if (activeSelection === "New Arrivals") {
      return productData.filter((product) => product.newArrival).slice(0, 6);
    }
    return productData.filter((product) => product.category === activeSelection).slice(0, 6);
  }, [activeSelection]);

  const favorites = productData.filter((product) => product.featured).slice(0, 3);
  const currentHero = heroSlides[activeHero];

  const handleNewsletter = (event) => {
    event.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterComplete(true);
    setNewsletterEmail("");
  };

  const showPreviousHero = () => {
    setActiveHero((current) => (current - 1 + heroSlides.length) % heroSlides.length);
  };

  const showNextHero = () => {
    setActiveHero((current) => (current + 1) % heroSlides.length);
  };

  return (
    <main className="main">
      <section className={`main-hero main-hero--${currentHero.align}`} aria-label="Featured collection">
        {heroSlides.map((slide, index) => (
          <img
            key={slide.image}
            className={`main-hero__image${index === activeHero ? " is-active" : ""}`}
            src={slide.image}
            alt=""
            width="1942"
            height="809"
            aria-hidden={index !== activeHero}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
          />
        ))}
        <div className="main-hero__shade" />
        <div className="main-hero__content">
          <span>{currentHero.eyebrow}</span>
          <h1>{currentHero.title}</h1>
          <p>{currentHero.copy}</p>
          <Link className="button button--accent" to={currentHero.link}>
            {currentHero.cta} <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
        <div className="main-hero__controls">
          <button type="button" onClick={showPreviousHero} aria-label="Previous slide"><FiArrowLeft /></button>
          <div>
            {heroSlides.map((slide, index) => (
              <button
                key={slide.title}
                type="button"
                className={index === activeHero ? "is-active" : ""}
                onClick={() => setActiveHero(index)}
                aria-label={`Show slide ${index + 1}`}
                aria-current={index === activeHero ? "true" : undefined}
              />
            ))}
          </div>
          <button type="button" onClick={showNextHero} aria-label="Next slide"><FiArrowRight /></button>
        </div>
      </section>

      <section className="main-categories page-width" aria-labelledby="category-heading">
        <div className="section-heading">
          <span className="eyebrow">The collection</span>
          <h2 id="category-heading">Built around the clothes you reach for.</h2>
        </div>
        <div className="main-categories__grid">
          {[
            ["Denim jeans", "Find your fit", "/img/products/p01-ankara.jpg", "/shop?category=Jeans"],
            ["Pants", "Shop utility", "/img/products/p09-fatigue-tan.jpg", "/shop?category=Pants"],
            ["Tops", "Layer up", "/img/products/p14-topper-denim.jpg", "/shop?category=Tops"],
            ["Accessories", "The finishing pieces", "/img/products/p21-watch-cap.jpg", "/shop?category=Accessories"],
          ].map(([title, label, image, link]) => (
            <Link className="main-categories__card" to={link} key={title}>
              <img src={image} alt="" width="1086" height="1448" loading="lazy" />
              <div>
                <h3>{title}</h3>
                <span>{label} <FiArrowRight aria-hidden="true" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="main-selection" aria-labelledby="selection-heading">
        <div className="page-width">
          <div className="section-heading section-heading--center">
            <span className="eyebrow">Everyday uniforms</span>
            <h2 id="selection-heading">Our Selection</h2>
          </div>
          <div className="main-selection__tabs" role="tablist" aria-label="Product categories">
            {selectionTabs.map((tab) => (
              <button
                key={tab}
                role="tab"
                type="button"
                className={activeSelection === tab ? "is-active" : ""}
                onClick={() => setActiveSelection(tab)}
                aria-selected={activeSelection === tab}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="main-selection__grid">
            {selectedProducts.map((product) => <ProductCard key={product.id} product={product} />)}
          </div>
          <Link className="button button--accent main-selection__all" to="/shop">
            Go to shop <FiArrowRight aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="main-weekly" aria-labelledby="weekly-heading">
        <div className="page-width">
          <div>
            <span className="eyebrow">A note from the workshop</span>
            <h2 id="weekly-heading">Don’t miss the item of the week.</h2>
            <p>One product gets the full story — and 20% off — every week.</p>
          </div>
          {newsletterComplete ? (
            <p className="main-weekly__success" role="status">Thanks — your first workshop note is on the way.</p>
          ) : (
            <form onSubmit={handleNewsletter}>
              <label className="sr-only" htmlFor="weekly-email">Email address</label>
              <input
                id="weekly-email"
                type="email"
                value={newsletterEmail}
                onChange={(event) => setNewsletterEmail(event.target.value)}
                placeholder="Enter your email"
                required
              />
              <button type="submit">Subscribe <FiArrowRight aria-hidden="true" /></button>
            </form>
          )}
        </div>
      </section>

      <section className="main-story" aria-labelledby="story-heading">
        <div className="page-width main-story__grid">
          <div className="main-story__image">
            <img src="/img/brand/story-makers.jpg" alt="Two denim makers reviewing a pair of jeans in a workshop" width="1448" height="1086" loading="lazy" />
          </div>
          <div className="main-story__content">
            <span className="eyebrow">Our story</span>
            <h2 id="story-heading">Built in San Francisco, worn everywhere.</h2>
            <p className="main-story__lead">Craftsmanship. Culture. Commitment.</p>
            <div>
              <h3>Who we are</h3>
              <p>{siteConfig.brandName} creates jeans and jackets for people who value durability, honest materials and timeless utility.</p>
            </div>
            <div>
              <h3>What we stand for</h3>
              <p>Good design starts with the cloth and ends with a garment that earns its place through years of wear.</p>
            </div>
            <div>
              <h3>Why it matters</h3>
              <p>This is not disposable fashion. It is gear made to work, travel and live in — season after season.</p>
            </div>
            <Link className="button button--accent" to="/about">Learn more about our journey <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      <section className="main-favorites page-width" aria-labelledby="favorites-heading">
        <div className="section-heading section-heading--center">
          <span className="eyebrow">The most worn</span>
          <h2 id="favorites-heading">Customer Favorites</h2>
        </div>
        <Link className="main-favorites__link" to="/shop">Go to shop <FiArrowRight aria-hidden="true" /></Link>
        <div className="main-favorites__grid">
          {favorites.map((product) => <ProductCard key={product.id} product={product} />)}
        </div>
      </section>

      <section className="main-testimonials" aria-labelledby="testimonials-heading">
        <div className="page-width">
          <div className="section-heading section-heading--center">
            <span className="eyebrow">From the field</span>
            <h2 id="testimonials-heading">What people are wearing in.</h2>
          </div>
          <div className="main-testimonials__grid">
            {testimonials.map((testimonial) => (
              <article key={testimonial.name}>
                <div><strong>{testimonial.name}</strong><span>★★★★★ <small>5.0</small></span></div>
                <p>“{testimonial.text}”</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="main-social" aria-labelledby="social-heading">
        <div className="section-heading section-heading--center">
          <span className="eyebrow">From the workshop</span>
          <h2 id="social-heading">Follow {siteConfig.brandName}</h2>
        </div>
        <div className="main-social__grid">
          {productData.slice(0, 6).map((product) => (
            <Link key={product.id} to={`/shopDetails/${product.id}`} aria-label={product.name}>
              <img src={product.image} alt="" width="1086" height="1448" loading="lazy" />
            </Link>
          ))}
        </div>
      </section>
    </main>
  );
}

export default Main;
